import { createSelector } from 'reselect';
import { createCachedSelector } from 're-reselect';
import { initialState } from './initialState';
import { get, min, max, map, isEmpty, find, orderBy } from 'lodash';
import * as d3 from 'd3';
import * as constants from './constants';
import { serializeTableData, serializeTableRowData } from 'ui-component/tables/serialization';
import { getDirectionalSurveyDataForSelectedUnit } from 'utils/directional_survey/main2';
import { ALL_TABLE_COLUMNS } from 'pages/drilling-completion/DrillingDesign/schema/table_columns';
import { P_TABLE_COLUMNS } from 'pages/drilling-completion/DrillingDesign/schema/table/pressure';
import { SCHEMA_SENS_HD_ANNULAR_VELOCITY } from 'pages/drilling-completion/DrillingDesign/schema/table/sens_hd_annular_velocity';
import { SCHEMA_SENS_HD_HYDRAULIC } from 'pages/drilling-completion/DrillingDesign/schema/table/sens_hd_hydraulic';
import { SCHEMA_SENS_DT_DNT } from 'pages/drilling-completion/DrillingDesign/schema/table/sens_dt_dnt';
import { SCHEMA_SENS_DT_SUMMARY } from 'pages/drilling-completion/DrillingDesign/schema/table/sens_dt_summary';
import { SCHEMA_CD_ENVELOPE } from 'pages/drilling-completion/DrillingDesign/schema/table/cd_envelope';
import { SCHEMA_CD_BURST_COLLAPSE } from 'pages/drilling-completion/DrillingDesign/schema/table/cd_burst_collapse';
import { SCHEMA_DT_DRAG_TORQUE } from 'pages/drilling-completion/DrillingDesign/schema/table/dt_dnt';
import { SCHEMA_DT_SUMMARY } from 'pages/drilling-completion/DrillingDesign/schema/table/dt_summary';
import { SCHEMA_HD_HYDRAULIC } from 'pages/drilling-completion/DrillingDesign/schema/table/hd_hydraulic';
import { SCHEMA_HD_SUMMARY } from 'pages/drilling-completion/DrillingDesign/schema/table/hd_summary';
import { SCHEMA_HD_V_ANNULAR } from 'pages/drilling-completion/DrillingDesign/schema/table/hd_v_annular';

const SCHEMA_CD_CASING_ENVELOPE_SENS = ALL_TABLE_COLUMNS.filter((d) => d.table_id === 'cd_casing_envelope_sens' && d.ui_show);
const SCHEMA_CD_BC_SENS = ALL_TABLE_COLUMNS.filter((d) => d.table_id === 'cd_burst_collapse_sens' && d.ui_show);

const getSchemaWithUom = (schema, uom) => {
  const newSchema = schema.map((col) => {
    const colId = get(col, 'col_id', null);
    const newCol = { ...col };
    if (!isEmpty(colId)) {
      newCol.col_unit = get(uom, `${colId}_uom`, '-');
    }
    return newCol;
  });
  return newSchema;
};

// CURRENT STORE SELECTORS
// ==================================================================================================

export const getStore = (store) => store[constants.NAMESPACE] || initialState;

export const currentLayoutSelector = (store) => getStore(store).currentLayout;

// Project
export const projectIdsSelector = (store) => getStore(store).projectIds;

export const projectsByIdSelector = (store) => getStore(store).projectsById;
export const selectedProjectIdsSelector = (store) => getStore(store).selectedProjectIds;

// Project Input
export const inputGeneralByProjectIdSelector = (store) => getStore(store).inputGeneralByProjectId;
export const inputPressureByProjectIdSelector = (store) => getStore(store).inputPressureByProjectId;
export const inputDrillingProgramByProjectIdSelector = (store) => getStore(store).inputDrillingProgramByProjectId;

// Project Output
export const outputCasingDesignByProjectIdSelector = (store) => getStore(store).outputCasingDesignByProjectId;
export const outputDragTorqueByProjectIdSelector = (store) => getStore(store).outputDragTorqueByProjectId;
export const outputHydraulicByProjectIdSelector = (store) => getStore(store).outputHydraulicByProjectId;

export const selectedCasingIdsSelector = (store) => getStore(store).selectedCasingIds;
export const selectedMudIdsSelector = (store) => getStore(store).selectedMudIds;
export const sensitivityIdsSelector = (store) => getStore(store).sensitivityIds;
export const sensitivitiesByIdSelector = (store) => getStore(store).sensitivitiesById;
export const hdSensitivitiesByIdSelector = (store) => getStore(store).hdSensitivitiesById;
export const cdSensitivitiesByIdSelector = (store) => getStore(store).cdSensitivitiesById;
export const dtSensitivitiesByIdSelector = (store) => getStore(store).dtSensitivitiesById;
export const selectedSensitivityIdsSelector = (store) => getStore(store).selectedSensitivityIds;
export const selectedHdScenarioIdsSelector = (store) => getStore(store).selectedHdScenarioIds;
export const selectedCdScenarioIdsSelector = (store) => getStore(store).selectedCdScenarioIds;
export const selectedDtScenarioIdsSelector = (store) => getStore(store).selectedDtScenarioIds;
export const settingsSelector = (store) => getStore(store).settings;
export const projectsSelector = createSelector(projectIdsSelector, projectsByIdSelector, (ids, byId) => ids.map((id) => byId[id]));

// CURRENT PROJECT SELECTORS
// ==================================================================================================

export const currentProjectIdSelector = createSelector(selectedProjectIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});
export const currentCasingIdSelector = createSelector(selectedCasingIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

// Note: this will limit how many case data will be rendered
export const renderProjectIdsSelector = createSelector(selectedProjectIdsSelector, (selectedProjectIds) => selectedProjectIds.slice(0, 10));
export const currentProjectSelector = createSelector(projectsByIdSelector, currentProjectIdSelector, (byId, id) => byId[id] || {});
export const currentGeneralSelector = createSelector(
  inputGeneralByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentPressureSelector = createSelector(
  inputPressureByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentDrillingProgramSelector = createSelector(
  inputDrillingProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentSurveyTblSelector = createSelector(currentPressureSelector, (obj) => {
  const data = getDirectionalSurveyDataForSelectedUnit(obj);
  const df = {
    columns: P_TABLE_COLUMNS.map((col) => col.col_id),
    data,
    schema: P_TABLE_COLUMNS
  };
  return serializeTableData(df);
});

export const currentCasingObjectByIdSelector = createSelector(currentDrillingProgramSelector, (drillingProgram) => {
  const byId = {};
  const casingPrograms = get(drillingProgram, 'casing_program', []);
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    byId[casingId] = casingProgram;
  });
  return byId;
});

// UI Dropdown
// ==================================================================================================

export const currentCasingOptionSelector = createSelector(currentDrillingProgramSelector, (drillingProgram) => {
  const casingPrograms = get(drillingProgram, 'casing_program', []);
  const casingOption = [
    {
      id: 'All',
      label: 'All Casings'
    }
  ];
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    const casingType = get(casingProgram, 'casing_type', 'UNKNOWN');
    const label = `${idx + 1} - ${casingType}`;
    casingOption.push({
      id: casingId,
      label
    });
  });
  return casingOption;
});

export const currentCasingIdsSelector = createSelector(currentDrillingProgramSelector, (drillingProgram) => {
  const casingPrograms = get(drillingProgram, 'casing_program', []);
  const casingIds = [];
  casingPrograms.forEach((casingProgram) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    casingIds.push(casingId);
  });
  return casingIds;
});

// CURRENT CASING DESIGN
// ==================================================================================================

export const currentCasingDesignSelector = createSelector(
  outputCasingDesignByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentSerializedCasingDesignSelector = createSelector(currentCasingDesignSelector, (casingDesign) => {
  const casingDesignById = get(casingDesign, 'casing_design_by_id', {});
  const casingIds = Object.keys(casingDesignById);
  const serializedData = {};
  casingIds.forEach((id) => {
    const casingDesign = casingDesignById[id];
    const { df_bc: dfBC, df_envelope: dfEnvelope } = casingDesign;
    serializedData[id] = {
      bcTbl: serializeTableRowData(dfBC),
      enTbl: serializeTableRowData(dfEnvelope)
    };
  });
  return serializedData;
});

export const currentCDEnvelopSelector = createSelector(currentCasingDesignSelector, (obj) => {
  const df = {
    columns: get(obj, 'en_cols', []),
    data: get(obj, 'en_data_uom', []),
    schema: SCHEMA_CD_ENVELOPE
  };
  return serializeTableData(df);
});

export const currentCDBurstCollapseSelector = createSelector(currentCasingDesignSelector, (obj) => {
  const df = {
    columns: get(obj, 'bc_cols', []),
    data: get(obj, 'bc_data_uom', []),
    schema: SCHEMA_CD_BURST_COLLAPSE
  };
  return serializeTableData(df);
});

// CURRENT DRAG TORQUE
// ==================================================================================================

export const currentDragTorqueSelector = createSelector(
  outputDragTorqueByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentDtSummaryUomSelector = createSelector(currentDragTorqueSelector, (obj) => get(obj, 'summary_uom', {}));
export const currentDtDragTorqueUomSelector = createSelector(currentDragTorqueSelector, (obj) => get(obj, 'drag_torque_uom', {}));

export const currentDtDragTorqueSelector = createSelector(currentDragTorqueSelector, (obj) => {
  const uom = get(obj, 'drag_torque_uom', {});
  const schema = getSchemaWithUom(SCHEMA_DT_DRAG_TORQUE, uom);
  const df = {
    columns: get(obj, 'drag_torque_cols', []),
    data: get(obj, 'drag_torque_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentDtSummarySelector = createSelector(currentDragTorqueSelector, (obj) => {
  const uom = get(obj, 'summary_uom', {});
  const schema = getSchemaWithUom(SCHEMA_DT_SUMMARY, uom);
  const df = {
    columns: get(obj, 'summary_cols', []),
    data: get(obj, 'summary_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentDtSummaryByCasingIdSelector = createSelector(currentDtSummarySelector, (obj) => {
  const rowData = get(obj, 'rowData', []);
  const byId = {};
  rowData.forEach((r) => {
    const casingId = get(r, 'casing_id', null);
    if (!isEmpty(casingId)) {
      byId[casingId] = r;
    }
  });
  return byId;
});

// CURRENT HYDRAULIC
// ==================================================================================================

export const currentHydraulicSelector = createSelector(
  outputHydraulicByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentHdSummaryUomSelector = createSelector(currentHydraulicSelector, (obj) => get(obj, 'summary_uom', {}));
export const currentHdHydraulicUomSelector = createSelector(currentHydraulicSelector, (obj) => get(obj, 'hydraulic_uom', {}));
export const currentHdVAnnularUomSelector = createSelector(currentHydraulicSelector, (obj) => get(obj, 'v_annular_uom', {}));

export const currentHdHydraulicSelector = createSelector(currentHydraulicSelector, (obj) => {
  const uom = get(obj, 'hydraulic_uom', {});
  const schema = getSchemaWithUom(SCHEMA_HD_HYDRAULIC, uom);
  const df = {
    columns: get(obj, 'hydraulic_cols', []),
    data: get(obj, 'hydraulic_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentHdSummarySelector = createSelector(currentHydraulicSelector, (obj) => {
  const uom = get(obj, 'summary_uom', {});
  const schema = getSchemaWithUom(SCHEMA_HD_SUMMARY, uom);
  const df = {
    columns: get(obj, 'summary_cols', []),
    data: get(obj, 'summary_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentHdVAnnularSelector = createSelector(currentHydraulicSelector, (obj) => {
  const uom = get(obj, 'v_annular_uom', {});
  const schema = getSchemaWithUom(SCHEMA_HD_V_ANNULAR, uom);
  const df = {
    columns: get(obj, 'v_annular_cols', []),
    data: get(obj, 'v_annular_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentHdSummaryByMudIdSelector = createSelector(currentHdSummarySelector, (obj) => {
  const byId = {};
  const rowData = get(obj, 'rowData', []);
  rowData.forEach((r) => {
    const mudId = get(r, 'mud_id', null);
    if (!isEmpty(mudId)) {
      byId[mudId] = r;
    }
  });
  return byId;
});

export const currentSerializedHydraulicSelector = createSelector(currentHydraulicSelector, (obj) => {
  const serializedData = {
    summaryUom: get(obj, `summary_uom`, null),
    hydraulicUom: get(obj, `hydraulic_uom`, null),
    vAnnularUom: get(obj, `v_annular_uom`, null),
    summaryData: serializeTableRowData(get(obj, `df_summary`, {})),
    hydraulicData: serializeTableRowData(get(obj, `df_hydraulic`, {})),
    vAnnularData: serializeTableRowData(get(obj, `df_v_annular`, {}))
  };
  return serializedData;
});

export const currentBaseMudProgramSelector = createSelector(
  currentCasingIdSelector,
  currentDrillingProgramSelector,
  (casingId, drillProgram) => {
    /*
  Create a base case Mud Program for sensitivity analysis (mud program at shoe depth)
  */
    const mudProgram = get(drillProgram, 'mud_program', []);
    const muds = mudProgram.filter((m) => m.casing_id === casingId);
    const sortedMuds = orderBy(muds, ['drilled_depth'], ['asc']);
    return sortedMuds[sortedMuds.length - 1];
  }
);

export const surveyDataSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'p_tbl', []));

export const currentMethodSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'directional_survey_method', undefined));

export const colorAttrSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_attr', null));

export const colorStopsSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_stops', null));

export const colorAttrMinSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMin = min(rowData.map((d) => d[colorAttr]));
  return attrMin;
});

export const colorAttrMaxSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMax = max(rowData.map((d) => d[colorAttr]));
  return attrMax;
});

export const currentCasingSelector = createSelector(
  currentCasingIdSelector,
  currentDrillingProgramSelector,
  (casingId, drillingProgram) => {
    /*
  Create a base case casing for sensitivity analysis
  */
    const casingProgram = get(drillingProgram, 'casing_program', []);
    const casings = casingProgram.filter((m) => m.id === casingId);
    const casing = get(casings, '[0].casing', null);
    return casing;
  }
);

// COMPARE CASES SELECTOR
// ==========================

export const dataByProjectIdSelector = createCachedSelector(
  projectsByIdSelector,
  inputPressureByProjectIdSelector,
  (state, projectId) => projectId,
  (projectsById, inputPressureByProjectId, projectId) => {
    // Survey Data
    const rowData = get(inputPressureByProjectId, `${projectId}.p_tbl`, []);
    const uom = get(inputPressureByProjectId, `${projectId}.p_tbl_uom`, []);
    const colorAttr = get(projectsById, `${projectId}.survey_chart.color_attr`, 'z');
    const colorStops = get(projectsById, `${projectId}.survey_chart.color_stops`, null);
    const name = get(projectsById, `${projectId}.name`, null);

    // Chart Data
    const colorAttrData = rowData.map((d) => d[colorAttr]);
    const x = rowData.map((d) => d.x);
    const y = rowData.map((d) => d.y);
    const z = rowData.map((d) => d.z);
    const minColorAttr = min(colorAttrData);
    const maxColorAttr = max(colorAttrData);
    const colorData = [];

    if (!isEmpty(colorStops)) {
      const offsets = map(colorStops, 'offset');
      const colors = map(colorStops, 'color');
      const colorScale = d3.scale.linear().domain(offsets).range(colors).interpolate(d3.interpolateRgb);
      colorAttrData.forEach((value) => {
        let color = '#0000ff';
        if (maxColorAttr > minColorAttr && colorScale) {
          const offset = (value - minColorAttr) / (maxColorAttr - minColorAttr);
          color = colorScale(offset);
        }
        colorData.push(color);
      });
    }

    return {
      rowData,
      x,
      y,
      z,
      maxColorAttr,
      minColorAttr,
      name,
      colorData,
      uom
    };
  }
)(
  (state, projectId) => projectId // Cache selectors by projectId
);

// CURRENT SENSITIVITY SELECTORS
// ==========================

export const currentSensitivityIdSelector = createSelector(selectedSensitivityIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

export const currentSensitivitySelector = createSelector(
  sensitivitiesByIdSelector,
  currentSensitivityIdSelector,
  (byId, id) => byId[id] || undefined
);

export const currentHSensitivitySelector = createSelector(
  hdSensitivitiesByIdSelector,
  currentSensitivityIdSelector,
  (byId, id) => byId[id] || undefined
);

export const currentCdSensitivitySelector = createSelector(
  cdSensitivitiesByIdSelector,
  currentSensitivityIdSelector,
  (byId, id) => byId[id] || undefined
);

export const currentDtSensitivitySelector = createSelector(
  dtSensitivitiesByIdSelector,
  currentSensitivityIdSelector,
  (byId, id) => byId[id] || undefined
);

export const sensitivitiesSelector = createSelector(sensitivityIdsSelector, sensitivitiesByIdSelector, (ids, byId) =>
  ids.map((id) => byId[id])
);

export const sensitivitiesBySelectedCasingIdSelector = createSelector(
  sensitivitiesSelector,
  selectedCasingIdsSelector,
  (sensitivities, selectedCasingIds) => sensitivities.filter((sens) => selectedCasingIds.includes(sens.casing_id))
);

export const currentHScenarioSelector = createSelector(currentHSensitivitySelector, (sensitivity) => {
  const df = {
    columns: get(sensitivity, 'scenario_cols', []),
    data: get(sensitivity, 'scenario_data', []),
    schema: get(sensitivity, 'scenario_schema', [])
  };
  return serializeTableData(df);
});

export const currentHAnnularSelector = createSelector(currentHSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'v_annular_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SENS_HD_ANNULAR_VELOCITY, uom);
  const df = {
    columns: get(sensitivity, 'v_annular_cols', []),
    data: get(sensitivity, 'v_annular_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentHHydraulicSelector = createSelector(currentHSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'hydraulic_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SENS_HD_HYDRAULIC, uom);
  const df = {
    columns: get(sensitivity, 'hydraulic_cols', []),
    data: get(sensitivity, 'hydraulic_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentSensitivitySummarySelector = createSelector(
  currentHScenarioSelector,
  currentHHydraulicSelector,
  (scenario, hydraulic) => {
    const scenarioData = get(scenario, 'rowData', []);
    const hydraulicData = get(hydraulic, 'rowData', []);
    const scenarioColDefs = get(scenario, 'columnDefs', []);
    const hydraulicColDefs = get(hydraulic, 'columnDefs', []);
    const scenarioPinnedData = get(scenario, 'pinnedTopRowData', []);
    const hydraulicPinnedData = get(hydraulic, 'pinnedTopRowData', []);
    const rowById = {};
    scenarioData.forEach((r) => {
      const rowId = `id-${r.scenario}`;
      rowById[rowId] = {
        param_1: r.param_1,
        param_2: r.param_2,
        param_3: r.param_3
      };
    });
    const summaryData = hydraulicData.map((r) => {
      const rowId = `id-${r.scenario}`;
      const row = rowById[rowId];
      return {
        ...r,
        ...row
      };
    });

    let columnDefs = [];

    if (scenarioColDefs.length > 0) {
      columnDefs = [...hydraulicColDefs, scenarioColDefs[1], scenarioColDefs[2], scenarioColDefs[3]];
    }

    return {
      columnDefs,
      rowData: summaryData,
      pinnedTopRowData: [{ ...scenarioPinnedData[0], ...hydraulicPinnedData[0] }]
    };
  }
);

export const currentCdScenarioSelector = createSelector(currentCdSensitivitySelector, (sensitivity) => {
  const df = {
    columns: get(sensitivity, 'scenario_cols', []),
    data: get(sensitivity, 'scenario_data', []),
    schema: get(sensitivity, 'scenario_schema', [])
  };
  return serializeTableData(df);
});

export const currentCdEnvelopeSelector = createSelector(currentCdSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'en_uom', {});
  const schema = getSchemaWithUom(SCHEMA_CD_CASING_ENVELOPE_SENS, uom);
  const df = {
    columns: get(sensitivity, 'en_cols', []),
    data: get(sensitivity, 'en_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentCdBurstCollapseSelector = createSelector(currentCdSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'bc_uom', {});
  const schema = getSchemaWithUom(SCHEMA_CD_BC_SENS, uom);
  const df = {
    columns: get(sensitivity, 'bc_cols', []),
    data: get(sensitivity, 'bc_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentDtScenarioSelector = createSelector(currentDtSensitivitySelector, (sensitivity) => {
  const df = {
    columns: get(sensitivity, 'scenario_cols', []),
    data: get(sensitivity, 'scenario_data', []),
    schema: get(sensitivity, 'scenario_schema', [])
  };
  return serializeTableData(df);
});

export const currentSenDtDragTorqueSelector = createSelector(currentDtSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'drag_torque_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SENS_DT_DNT, uom);
  const df = {
    columns: get(sensitivity, 'drag_torque_cols', []),
    data: get(sensitivity, 'drag_torque_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentSenDtSummarySelector = createSelector(currentDtSensitivitySelector, (sensitivity) => {
  const uom = get(sensitivity, 'summary_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SENS_DT_SUMMARY, uom);
  const df = {
    columns: get(sensitivity, 'summary_cols', []),
    data: get(sensitivity, 'summary_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

export const currentDTDrillPipeSelector = createSelector(currentDtSensitivitySelector, (sensitivity) => {
  const df = get(sensitivity, 'df_dnt_drill_pipe', {});
  return serializeTableData(df);
});

export const currentDTCasingSelector = createSelector(currentDtSensitivitySelector, (sensitivity) => {
  const df = get(sensitivity, 'df_dnt_casing', {});
  return serializeTableData(df);
});

// Unuse
export const currentHydraulicProgramSelector = createSelector(currentProjectSelector, (currentProject) => {
  // const casingPrograms = get(currentProject, 'casing_program', []);
  const mudPrograms = get(currentProject, 'mud_program', []);
  const hydraulicByMudId = get(currentProject, 'hydraulic_by_mud_id', {});

  const hydraulicProgram = [];
  mudPrograms.forEach((mud) => {
    const mudId = get(mud, 'id', null);
    // const casingId = get(mud, 'casing_id', null);
    const oneRow = {
      drilled_depth: get(mud, 'drilled_depth', null),
      q: get(mud, 'q', null),
      den: get(mud, 'den', null),
      vis: get(mud, 'vis', null),
      fluid_model: get(mud, 'fluid_model', null),
      ty: get(mud, 'ty', null),
      K: get(mud, 'K', null),
      n: get(mud, 'n', null),
      drilled_tvd: get(hydraulicByMudId, `${mudId}.drilled_tvd`, null),
      V_kick: get(hydraulicByMudId, `${mudId}.V_kick`, null),
      h_kick: get(hydraulicByMudId, `${mudId}.h_kick`, null),
      P_surf: get(hydraulicByMudId, `${mudId}.P_surf`, null)
    };
    hydraulicProgram.push(oneRow);
  });

  const columns = [
    {
      field: 'drilled_depth',
      headerName: 'Drilled Depth (ft)'
    },
    {
      field: 'q',
      headerName: 'Flow Rate (gpm)'
    },
    {
      field: 'den',
      headerName: 'Fluid Density (ppg)'
    },
    {
      field: 'vis',
      headerName: 'Fluid Viscosity (cp)'
    },
    {
      field: 'fluid_model',
      headerName: 'Fluid Model'
    },
    {
      field: 'ty',
      headerName: 'Yield ty'
    },
    {
      field: 'K',
      headerName: 'K'
    },
    {
      field: 'n',
      headerName: 'n'
    },
    {
      field: 'drilled_tvd',
      headerName: 'Drilled TVD (ft)'
    },
    {
      field: 'V_kick',
      headerName: 'Kick Volume'
    },
    {
      field: 'h_kick',
      headerName: 'h_kick'
    },
    {
      field: 'P_surf',
      headerName: 'P_surf'
    }
  ];

  const columnDefs = columns.map((d) => ({
    field: d.field,
    headerName: d.headerName,
    tooltipField: d.field,
    headerTooltip: d.headerName,
    minWidth: 80
  }));

  return {
    columnDefs,
    rowData: hydraulicProgram
  };
});
